import { formatStockMoney } from "../../../../utility/stockValuation";
import "./StockModal.scss";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAlert } from "react-alert";
import dayjs from "dayjs";
import StockModal from "./StockModal";
import SkeletonLoader from "../../../../components/skeletonLoader/SkeletonLoader";
import { getStockMovementsPage } from "../../../../utility/getStock";
import { formatQuantity } from "../../../../utility/inputValidation";
import { getStockLossTone } from "../../../../utility/stockLossStats";

const DATE_FORMAT = "DD MMM YYYY HH:mm";
const MOVEMENTS_PAGE_SIZE = 50;

const MovementLine = ({ movement, reasonLabels, costLabel, valueLabel }) => (
  <div className="product-line stock-movement-line">
    <p className="product-line-label">
      {movement?.createdAt ? dayjs(movement?.createdAt).format(DATE_FORMAT) : "-"}
    </p>
    <p className="product-line-value">{reasonLabels || "-"}</p>
    <p className="product-line-value">{formatQuantity(movement?.delta)}</p>
    <p className="product-line-value"><span>{costLabel}: </span>{formatStockMoney(movement?.unitCost)}</p>
    <p className="product-line-value"><span>{valueLabel}: </span>{formatStockMoney(movement?.value)}</p>
  </div>
);

export default function MovementsModal({ open, onClose, product, venueId, lossStats }) {
  const { t } = useTranslation("stockManagement");
  const alert = useAlert();
  const [movements, setMovements] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);

  const fetchMovements = useCallback(
    async ({ signal, cursor = null, append = false } = {}) => {
      if (!product?.id || !venueId) return;

      if (append) setIsLoadingMore(true);
      else setIsLoading(true);
      setHasLoadError(false);
      try {
        if (signal?.aborted) return;
        const page = await getStockMovementsPage({
          venueId,
          stockItemId: product.id,
          pageSize: MOVEMENTS_PAGE_SIZE,
          cursor,
        });
        if (signal?.aborted) return;
        setMovements((previousMovements) =>
          append ? [...previousMovements, ...page.movements] : page.movements,
        );
        setLastDoc(page.lastDoc);
        setHasMore(page.hasMore);
      } catch (error) {
        if (signal?.aborted) return;
        console.log("Error while fetching stock movements: " + error);
        alert.show(t("stock_movements_load_error"), { type: "error" });
        setHasLoadError(true);
        if (!append) setMovements([]);
      } finally {
        if (signal?.aborted) return;
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [alert, product?.id, t, venueId],
  );

  useEffect(() => {
    const abortController = new AbortController();
    if (open) {
      fetchMovements({ signal: abortController.signal });
    } else {
      setMovements([]);
      setLastDoc(null);
      setHasMore(false);
      setIsLoading(false);
      setIsLoadingMore(false);
      setHasLoadError(false);
    }
    return () => abortController.abort();
  }, [open, fetchMovements]);

  const loadMoreMovements = () => {
    if (!lastDoc || isLoadingMore) return;
    fetchMovements({ cursor: lastDoc, append: true });
  };

  const reasonLabels = {
    sale: t("movements_modal_reason_sale"),
    sale_adjustment: t("movements_modal_reason_sale_adjustment"),
    cancel: t("movements_modal_reason_cancel"),
    restock: t("movements_modal_reason_restock"),
    initial_stock: t("movements_modal_reason_initial_stock"),
    inventory_adjustment: t("movements_modal_reason_inventory_adjustment"),
    waste: t("movements_modal_reason_waste"),
  };

  const normalizedLossStats = useMemo(() => lossStats || {}, [lossStats]);
  const lossRateLabel = Number.isFinite(normalizedLossStats.lossRate)
    ? `${normalizedLossStats.lossRate.toFixed(1)}%`
    : "-";
  const lossTone = getStockLossTone(normalizedLossStats.lossRate);

  return (
    <StockModal
      open={open}
      onClose={onClose}
      title={t("movements_modal_title", { name: product?.name ? ` - ${product.name}` : "" })}
    >
      <div className="stock-action-modal-products">
        <p className="stock-action-modal-products-subtitle">{t("movements_modal_subtitle")}</p>

        {isLoading ? (
          <div className="stock-action-modal-products-container">
            <SkeletonLoader className="movements-skeleton" />
            <SkeletonLoader className="movements-skeleton" />
            <SkeletonLoader className="movements-skeleton" />
            <SkeletonLoader className="movements-skeleton" />
            <SkeletonLoader className="movements-skeleton" />
            <SkeletonLoader className="movements-skeleton" />
          </div>
        ) : movements?.length > 0 ? (
          <>
            <div className="stock-loss-summary">
              <div className="stock-loss-summary-card">
                <span>{t("movements_modal_loss_sold_since_last_inventory")}</span>
                <strong>{formatQuantity(normalizedLossStats.soldQuantity)}</strong>
              </div>
              <div className="stock-loss-summary-card">
                <span>{t("movements_modal_loss_inventory_delta")}</span>
                <strong>{formatQuantity(normalizedLossStats.inventoryDelta)}</strong>
              </div>
              <div className={`stock-loss-summary-card stock-loss-summary-card-${lossTone}`}>
                <span>{t("movements_modal_loss_rate")}</span>
                <strong>{normalizedLossStats.hasInventoryCount ? lossRateLabel : "-"}</strong>
              </div>
            </div>
            <div className="stock-action-modal-products-container">
              {movements.map((movement) => (
                <MovementLine
                  costLabel={t("stock_unit_cost")} valueLabel={t("stock_movement_value")}
                  key={movement.id}
                  movement={movement}
                  reasonLabels={reasonLabels[movement?.reason] || movement?.reason}
                />
              ))}
              {hasMore && (
                <button
                  type="button"
                  className="stock-movements-load-more"
                  disabled={isLoadingMore}
                  onClick={loadMoreMovements}
                >
                  {isLoadingMore
                    ? t("movements_modal_loading_more")
                    : t("movements_modal_load_more")}
                </button>
              )}
            </div>
          </>
        ) : hasLoadError ? (
          <div className="stock-action-modal-products-empty-container">
            <p>{t("stock_movements_load_error")}</p>
          </div>
        ) : (
          <div className="stock-action-modal-products-empty-container">
            <p>{t("movements_modal_empty")}</p>
          </div>
        )}
      </div>
    </StockModal>
  );
}
